import React from 'react';
import { formatDublinDateTime } from '../../utils/date';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { MedicationEntryType, ParentSafeMedicationEntry } from '../../types';

const ENTRY_TYPE_LABELS: Record<MedicationEntryType, string> = {
  [MedicationEntryType.ADMINISTERED]: 'Given',
  [MedicationEntryType.REFUSED]: 'Refused by child',
  [MedicationEntryType.MISSED]: 'Missed',
  [MedicationEntryType.PARTIAL]: 'Partial dose',
  [MedicationEntryType.CORRECTION]: 'Correction',
  [MedicationEntryType.EMERGENCY_USE]: 'Emergency use',
};

const ENTRY_TYPE_CLASSES: Record<MedicationEntryType, string> = {
  [MedicationEntryType.ADMINISTERED]: 'bg-green-100 text-green-800',
  [MedicationEntryType.REFUSED]: 'bg-amber-100 text-amber-800',
  [MedicationEntryType.MISSED]: 'bg-red-100 text-red-800',
  [MedicationEntryType.PARTIAL]: 'bg-amber-100 text-amber-800',
  [MedicationEntryType.CORRECTION]: 'bg-blue-100 text-blue-800',
  [MedicationEntryType.EMERGENCY_USE]: 'bg-red-100 text-red-800',
};

function formatDateTime(iso: string): string {
  return formatDublinDateTime(iso);
}

/**
 * The parent's read-only view of one requirement's administration log.
 *
 * Read-only by construction: a parent never logs a dose, so there is no
 * mutation in this file. Entries arrive newest-first from the server's log
 * query; the order is not re-sorted here so the family sees the same sequence
 * an inspector would.
 *
 * Two facts are surfaced as explicit flags rather than buried in the notes,
 * because both are things a parent has a right to notice without reading
 * carefully:
 *   - `unwitnessedReason` present → only one staff member signed for the dose.
 *   - `administeredPastExpiry` → the medication was past its expiry date.
 * A CORRECTION entry never replaces the entry it corrects — both stay visible,
 * which is the whole point of an append-only log.
 */
const MedicationLog: React.FC<{ entries: ParentSafeMedicationEntry[] }> = ({ entries }) => {
  if (entries.length === 0) {
    return (
      <p className="text-sm text-gray-500" data-testid="medication-log-empty">
        No doses have been recorded yet.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-gray-100" data-testid="medication-log">
      {entries.map((entry) => (
        <li
          key={entry.entryId}
          data-testid={`medication-log-entry-${entry.entryId}`}
          className="py-2.5"
        >
          <div className="flex items-start gap-2 flex-wrap">
            <span
              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                ENTRY_TYPE_CLASSES[entry.entryType]
              }`}
            >
              {ENTRY_TYPE_LABELS[entry.entryType]}
            </span>
            <span className="text-sm text-gray-900">{formatDateTime(entry.administeredAt)}</span>
            {entry.doseGiven && <span className="text-sm text-gray-500">{entry.doseGiven}</span>}
          </div>

          <div className="mt-1 text-xs text-gray-500">
            By {entry.administeredByName}
            {entry.witnessStaffName ? `, witnessed by ${entry.witnessStaffName}` : ''}
            {entry.scheduledSlot ? ` · scheduled ${entry.scheduledSlot}` : ''}
          </div>

          {entry.unwitnessedReason && (
            <div
              data-testid="medication-unwitnessed-flag"
              className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5"
            >
              <ExclamationTriangleIcon aria-hidden="true" className="h-4 w-4 flex-shrink-0" />
              <span>Given without a second staff witness — {entry.unwitnessedReason}</span>
            </div>
          )}

          {entry.administeredPastExpiry && (
            <div
              data-testid="medication-past-expiry-flag"
              className="mt-1.5 flex items-start gap-1.5 text-xs text-red-800 bg-red-50 border border-red-200 rounded-md px-2 py-1.5"
            >
              <ExclamationTriangleIcon aria-hidden="true" className="h-4 w-4 flex-shrink-0" />
              <span>The medication was past its expiry date when this dose was given.</span>
            </div>
          )}

          {entry.correctsEntryId && (
            <div className="mt-1 text-xs text-blue-700">
              Corrects an earlier entry. The original stays in the log.
            </div>
          )}

          {entry.notes && <p className="mt-1 text-xs text-gray-600">{entry.notes}</p>}
        </li>
      ))}
    </ul>
  );
};

export default MedicationLog;
